import React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { func, string } from 'prop-types';
import { prop } from 'rambdax';
import styled from 'styled-components';
import { SearchIcon } from '@/assets/images';

const StyledSearchInput = styled(TextField).withConfig({
  shouldForwardProp: (propArg) => !['backgroundColor'].includes(propArg),
})`
  width: 100%;
  
  .MuiOutlinedInput-root {
    background-color: ${prop('backgroundColor')};
    height: 48px;
  }

  input[type="search"]::-webkit-search-cancel-button {
    display: none;
  }

  .MuiInputBase-input,
  .MuiInputBase-input::-webkit-input-placeholder {
    opacity: 1;
  }
  .MuiInputBase-input:focus::-webkit-input-placeholder {
    opacity: 0;
  }
}
`;

const transparentGreyCustom2 = 'rgba(243, 245, 245, 0.6)';

function SearchInput({
  backgroundColor,
  className,
  id,
  onChange,
  placeholder,
  value,
  ...rest
}) {
  const ref = React.useRef(null);
  const [cursor, setCursor] = React.useState(null);

  React.useEffect(() => {
    // This serves to preserve text cursor position for controlled inputs.
    // See https://stackoverflow.com/a/68928267
    const input = ref.current;
    if (input) input.setSelectionRange(cursor, cursor);
  }, [ref, cursor, value]);

  const handleChange = React.useCallback(
    ({ target }) => {
      setCursor(target.selectionStart);
      onChange(target?.value);
    },
    [onChange],
  );

  return (
    <StyledSearchInput
      backgroundColor={backgroundColor}
      className={className}
      placeholder={placeholder}
      margin="none"
      name="search-input"
      onChange={handleChange}
      type="search"
      size="small"
      value={value}
      inputRef={ref}
      InputProps={{
        autoComplete: 'off',
        'data-testid': id,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon className="text-grey-900" />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
}

SearchInput.propTypes = {
  backgroundColor: string,
  className: string,
  id: string,
  onChange: func.isRequired,
  placeholder: string.isRequired,
  value: string,
};

SearchInput.defaultProps = {
  backgroundColor: transparentGreyCustom2,
  className: '',
  id: 'search-input',
  value: '',
};

export default SearchInput;
